import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useShakatahCartStore } from '@/stores/shakatahCartStore';

/**
 * Hook to sync split cart data to the database for abandoned cart reminders.
 * Only syncs for authenticated users.
 */
export function useAbandonedCartSync() {
  const items = useShakatahCartStore((state) => state.items);
  const previousItemsRef = useRef<string>('');
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const syncCartToDatabase = async () => {
      // Get current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        // User not logged in, don't sync
        return;
      }

      const userId = session.user.id;
      const userEmail = session.user.email;

      if (!userEmail) {
        console.warn('User has no email, cannot sync abandoned cart');
        return;
      }

      // Serialize items for comparison
      const itemsJson = JSON.stringify(items);
      
      // Skip if items haven't changed
      if (itemsJson === previousItemsRef.current) {
        return;
      }
      
      previousItemsRef.current = itemsJson;

      try {
        if (items.length === 0) {
          // Cart is empty - mark as checked out or delete
          const { error } = await supabase
            .from('abandoned_split_carts')
            .update({ 
              status: 'checked_out',
              checked_out_at: new Date().toISOString()
            })
            .eq('user_id', userId)
            .eq('status', 'active');

          if (error && error.code !== 'PGRST116') {
            console.error('Error updating abandoned cart status:', error);
          }
        } else {
          // Get store domain from first item if available
          const storeDomain = null; // Will be set from product data if available

          // Check if there's an existing active cart for this user
          const { data: existingCart } = await supabase
            .from('abandoned_split_carts')
            .select('id')
            .eq('user_id', userId)
            .eq('status', 'active')
            .single();

          if (existingCart) {
            // Update existing cart
            const { error } = await supabase
              .from('abandoned_split_carts')
              .update({
                cart_items: JSON.parse(JSON.stringify(items)),
                updated_at: new Date().toISOString(),
              })
              .eq('id', existingCart.id);

            if (error) {
              console.error('Error updating abandoned cart:', error);
            }
          } else {
            // Insert new abandoned cart record
            const { error } = await supabase
              .from('abandoned_split_carts')
              .insert([{
                user_id: userId,
                user_email: userEmail,
                cart_items: JSON.parse(JSON.stringify(items)),
                store_domain: storeDomain,
              }]);

            if (error) {
              console.error('Error inserting abandoned cart:', error);
            }
          }
        }
      } catch (err) {
        console.error('Error syncing abandoned cart:', err);
      }
    };

    // Debounce the sync to avoid too many database calls
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      syncCartToDatabase();
    }, 2000); // Wait 2 seconds after last change before syncing

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [items]);
};
